import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Pencil, Trash2, Upload, Package, Tag, Loader2, 
  ArrowLeft, Image as ImageIcon, X, Check, GripVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../lib/adminApi';
import { toast } from 'sonner';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Modals
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  // Edit states
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Form states
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    category: '',
    sizes: '',
    colors: '',
    is_new: false,
    is_sale: false,
    stock: ''
  });
  const [categoryForm, setCategoryForm] = useState({ id: '', name: '' });
  
  // Multiple images state
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const checkAdminAndLoadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.all([
        adminApi.getProducts(),
        adminApi.getCategories()
      ]);
      // Ensure we have arrays
      setProducts(Array.isArray(productsData) ? productsData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setIsAdmin(true);
    } catch (error) {
      console.error('Admin load error:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        const detail = error.response?.data?.detail;
        const errorMsg = typeof detail === 'string' ? detail : 
                         Array.isArray(detail) ? detail.map(e => e.msg || String(e)).join(', ') :
                         'Failed to load admin data';
        toast.error(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
      return;
    }
    
    if (user) {
      checkAdminAndLoadData();
    }
  }, [authLoading, isAuthenticated, user, navigate, checkAdminAndLoadData]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setImageFiles(prev => [...prev, ...files]);
      
      // Generate previews for new files
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, { url: reader.result, isNew: true, file }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index, isExisting) => {
    if (isExisting) {
      setExistingImages(prev => prev.filter((_, i) => i !== index));
    } else {
      const previewIndex = imagePreviews.findIndex((p, i) => p.isNew && i === index);
      setImagePreviews(prev => prev.filter((_, i) => i !== index));
      // Also remove from files
      const fileIndex = imageFiles.findIndex(f => imagePreviews[index]?.file === f);
      if (fileIndex > -1) {
        setImageFiles(prev => prev.filter((_, i) => i !== fileIndex));
      }
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      price: '',
      original_price: '',
      category: '',
      sizes: '',
      colors: '',
      is_new: false,
      is_sale: false,
      stock: ''
    });
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages([]);
    setEditingProduct(null);
  };

  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        original_price: product.original_price?.toString() || '',
        category: product.category,
        sizes: product.sizes.join(', '),
        colors: product.colors.join(', '),
        is_new: product.is_new,
        is_sale: product.is_sale,
        stock: product.stock.toString()
      });
      // Set existing images
      const imgs = product.images || [product.image];
      setExistingImages(imgs.filter(Boolean));
      setImagePreviews([]);
      setImageFiles([]);
    } else {
      resetProductForm();
    }
    setProductModalOpen(true);
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ id: category.id, name: category.name });
    } else {
      setEditingCategory(null);
      setCategoryForm({ id: '', name: '' });
    }
    setCategoryModalOpen(true);
  };

  const openDeleteModal = (type, item) => {
    setDeleteTarget({ type, item });
    setDeleteModalOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price || !productForm.category) {
      toast.error('Please fill in required fields');
      return;
    }

    setIsSaving(true);
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        original_price: productForm.original_price ? parseFloat(productForm.original_price) : null,
        stock: parseInt(productForm.stock) || 0,
        image_urls: existingImages.join(',')
      };

      if (editingProduct) {
        await adminApi.updateProduct(editingProduct.id, data, imageFiles);
        toast.success('Product updated');
      } else {
        await adminApi.createProduct(data, imageFiles);
        toast.success('Product created');
      }

      setProductModalOpen(false);
      resetProductForm();
      checkAdminAndLoadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') :
                       'Failed to save product';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) {
      toast.error('Please enter a category name');
      return;
    }

    if (!categoryForm.id) {
      toast.error('Please enter a category ID');
      return;
    }

    setIsSaving(true);
    try {
      if (editingCategory) {
        // Pass the new ID if it changed
        const newId = categoryForm.id !== editingCategory.id ? categoryForm.id : null;
        await adminApi.updateCategory(editingCategory.id, categoryForm.name, newId);
        toast.success('Category updated');
      } else {
        await adminApi.createCategory(categoryForm);
        toast.success('Category created');
      }

      setCategoryModalOpen(false);
      setCategoryForm({ id: '', name: '' });
      setEditingCategory(null);
      checkAdminAndLoadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') :
                       'Failed to save category';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setIsSaving(true);
    try {
      if (deleteTarget.type === 'product') {
        await adminApi.deleteProduct(deleteTarget.item.id);
        toast.success('Product deleted');
      } else {
        await adminApi.deleteCategory(deleteTarget.item.id);
        toast.success('Category deleted');
      }

      setDeleteModalOpen(false);
      setDeleteTarget(null);
      checkAdminAndLoadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') :
                       'Failed to delete';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <main className="min-h-screen pt-20 bg-coconut flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent-ui" />
      </main>
    );
  }

  if (!isAdmin) {
    return null;
  }

  // Combine existing images and new previews for display
  const allImages = [
    ...existingImages.map((url, i) => ({ url, isExisting: true, index: i })),
    ...imagePreviews.map((p, i) => ({ url: p.url, isExisting: false, index: i }))
  ];

  return (
    <main className="min-h-screen pt-20 bg-coconut">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-text-subtle hover:text-text-primary transition-colors mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Store
            </button>
            <h1 className="font-display text-3xl font-bold text-text-primary">Admin Dashboard</h1>
            <p className="text-text-subtle mt-1">Manage products and categories</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-white border border-stone-200">
            <TabsTrigger value="products" className="data-[state=active]:bg-accent-ui data-[state=active]:text-white">
              <Package className="h-4 w-4 mr-2" />
              Products ({products.length})
            </TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-accent-ui data-[state=active]:text-white">
              <Tag className="h-4 w-4 mr-2" />
              Categories ({categories.length})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => openProductModal()} className="bg-accent-ui hover:bg-accent-ui/90 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>

            <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-text-primary">Images</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-text-primary">Product</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-text-primary">Category</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-text-primary">Price</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-text-primary">Stock</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-text-primary">Status</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-stone-50">
                      <td className="px-4 py-3">
                        <div className="flex -space-x-2">
                          {(product.images || [product.image]).slice(0, 3).map((img, i) => (
                            <div key={i} className="w-10 h-10 bg-stone-100 rounded border-2 border-white overflow-hidden">
                              <img src={img} alt="" className="w-full h-full object-cover" />
                            </div>
                          ))}
                          {(product.images?.length || 1) > 3 && (
                            <div className="w-10 h-10 bg-stone-200 rounded border-2 border-white flex items-center justify-center text-xs font-medium text-text-subtle">
                              +{(product.images?.length || 1) - 3}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-text-primary">{product.name}</p>
                        <p className="text-xs text-text-subtle truncate max-w-[200px]">{product.description}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{product.category}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium">${product.price.toFixed(2)}</p>
                        {product.original_price && (
                          <p className="text-xs text-text-subtle line-through">${product.original_price.toFixed(2)}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-subtle">{product.stock}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {product.is_new && <Badge className="bg-accent-ui text-white text-xs">New</Badge>}
                          {product.is_sale && <Badge className="bg-tangerine text-white text-xs">Sale</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openProductModal(product)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => openDeleteModal('product', product)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => openCategoryModal()} className="bg-accent-ui hover:bg-accent-ui/90 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div key={category.id} className="bg-white rounded-lg border border-stone-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-text-primary">{category.name}</h3>
                      <p className="text-sm text-text-subtle">ID: {category.id}</p>
                      <p className="text-sm text-text-subtle">{category.count} products</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openCategoryModal(category)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600 hover:bg-red-50" 
                        onClick={() => openDeleteModal('category', category)}
                        disabled={category.count > 0}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Product Modal */}
      <Dialog open={productModalOpen} onOpenChange={setProductModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Multiple Image Upload */}
            <div>
              <Label>Product Images (Multiple)</Label>
              <div className="mt-2">
                {/* Image Grid */}
                {allImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {allImages.map((img, idx) => (
                      <div key={idx} className="relative aspect-square bg-stone-100 rounded-lg overflow-hidden group">
                        <img src={img.url} alt="" className="w-full h-full object-cover" />
                        {idx === 0 && (
                          <span className="absolute top-1 left-1 bg-accent-ui text-white text-xs px-1.5 py-0.5 rounded">
                            Main
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(img.index, img.isExisting)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload">
                    <Button type="button" variant="outline" className="cursor-pointer" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Images
                      </span>
                    </Button>
                  </label>
                  <span className="text-sm text-text-subtle">
                    {allImages.length} image{allImages.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                
                <p className="text-xs text-text-subtle mt-2">
                  First image will be the main product image. You can upload multiple images.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-stone-300 rounded-md"
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                className="mt-1 w-full h-20 px-3 py-2 border border-stone-300 rounded-md resize-none"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="price">Price *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="original_price">Original Price</Label>
                <Input
                  id="original_price"
                  type="number"
                  step="0.01"
                  placeholder="For sale items"
                  value={productForm.original_price}
                  onChange={(e) => setProductForm({ ...productForm, original_price: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="stock">Stock</Label>
                <Input
                  id="stock"
                  type="number"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sizes">Sizes (comma-separated)</Label>
                <Input
                  id="sizes"
                  placeholder="XS, S, M, L, XL"
                  value={productForm.sizes}
                  onChange={(e) => setProductForm({ ...productForm, sizes: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="colors">Colors (comma-separated)</Label>
                <Input
                  id="colors"
                  placeholder="Black, White, Navy"
                  value={productForm.colors}
                  onChange={(e) => setProductForm({ ...productForm, colors: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_new"
                  checked={productForm.is_new}
                  onCheckedChange={(checked) => setProductForm({ ...productForm, is_new: checked })}
                />
                <Label htmlFor="is_new">New Arrival</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_sale"
                  checked={productForm.is_sale}
                  onCheckedChange={(checked) => setProductForm({ ...productForm, is_sale: checked })}
                />
                <Label htmlFor="is_sale">On Sale</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProduct} disabled={isSaving} className="bg-accent-ui hover:bg-accent-ui/90 text-white">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              {editingProduct ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="cat-id">Category ID (lowercase, no spaces)</Label>
              <Input
                id="cat-id"
                placeholder="e.g., shoes, handbags"
                value={categoryForm.id}
                onChange={(e) => setCategoryForm({ ...categoryForm, id: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                className="mt-1"
              />
              {editingCategory && categoryForm.id !== editingCategory.id && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Changing ID will update all products in this category
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="cat-name">Category Name</Label>
              <Input
                id="cat-name"
                placeholder="e.g., Shoes, Handbags"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} disabled={isSaving} className="bg-accent-ui hover:bg-accent-ui/90 text-white">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-red-600">Confirm Delete</DialogTitle>
          </DialogHeader>

          <p className="py-4">
            Are you sure you want to delete this {deleteTarget?.type}?
            <br />
            <strong>{deleteTarget?.item?.name}</strong>
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={isSaving} className="bg-red-600 hover:bg-red-700 text-white">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default AdminPage;
